import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { getUser, saveUser, logout } from '../auth';
import { getBadgesUtilisateur } from '../badges';

const calculerStreak = (participations) => {
  if (!participations || participations.length === 0) return 0;
  const moisParticipes = new Set(
    participations.map(p => {
      const d = new Date(p.created_at);
      return `${d.getFullYear()}-${d.getMonth()}`;
    })
  );
  const maintenant = new Date();
  let streak = 0;
  for (let i = 0; i < 12; i++) {
    const d = new Date(maintenant.getFullYear(), maintenant.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (moisParticipes.has(key)) streak++;
    else if (i > 0) break;
  }
  return streak;
};

export default function Profil() {
  const navigate = useNavigate();
  const [profil, setProfil] = useState(null);
  const [enfants, setEnfants] = useState([]);
  const [badges, setBadges] = useState([]);
  const [activitesCrees, setActivitesCrees] = useState([]);
  const [activitesRejointes, setActivitesRejointes] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [participations, setParticipations] = useState([]);
  const [onglet, setOnglet] = useState('activites');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [userId, setUserId] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    const user = getUser();
    if (!user) { navigate('/connexion'); return; }
    setUserId(user.id);
    // Toujours recharger depuis Supabase au montage
    fetchProfilFrais(user.id);
  }, []);

  const fetchProfilFrais = async (uid) => {
    // Charge depuis Supabase en priorité — jamais depuis localStorage
    const { data } = await supabase.from('users').select('*').eq('id', uid).single();
    if (data) {
      setProfil(data);
      saveUser(data); // Met à jour le localStorage avec les données fraîches
      fetchEnfants(uid);
      fetchBadges(uid);
      fetchActivites(uid);
      fetchNotifications(uid);
      fetchParticipations(uid);
    }
  };

  const fetchEnfants = async (uid) => {
    const { data } = await supabase.from('enfants').select('*').eq('user_id', uid);
    if (data) setEnfants(data);
  };

  const fetchBadges = async (uid) => {
    const data = await getBadgesUtilisateur(uid);
    setBadges(data);
  };

  const fetchActivites = async (uid) => {
    const { data: crees } = await supabase.from('activites').select('*').eq('user_id', uid).order('date', { ascending: false });
    const { data: parts } = await supabase.from('participations').select('*, activites(*)').eq('user_id', uid);
    if (crees) setActivitesCrees(crees);
    if (parts) setActivitesRejointes(parts.map(p => p.activites).filter(Boolean));
  };

  const fetchNotifications = async (uid) => {
    const { data } = await supabase.from('notifications').select('*').eq('user_id', uid).order('created_at', { ascending: false }).limit(20);
    if (data) {
      setNotifications(data);
      await supabase.from('notifications').update({ lu: true }).eq('user_id', uid).eq('lu', false);
    }
  };

  const fetchParticipations = async (uid) => {
    const { data } = await supabase.from('participations').select('*').eq('user_id', uid);
    if (data) setParticipations(data);
  };

  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file || !userId) return;
    setUploadingPhoto(true);
    try {
      // Toujours le même nom de fichier → même URL → pas de problème de cache
      const path = `avatars/${userId}.jpg`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(path, file, { upsert: true, contentType: 'image/jpeg' });
      
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
      // On force le refresh avec un timestamp UNIQUE stocké en base
      const avatarUrl = `${urlData.publicUrl}?v=${Date.now()}`;
      
      await supabase.from('users').update({ avatar_url: avatarUrl }).eq('id', userId);
      
      // Recharger le profil complet depuis Supabase
      const { data: updatedUser } = await supabase.from('users').select('*').eq('id', userId).single();
      if (updatedUser) {
        setProfil(updatedUser);
        saveUser(updatedUser);
      }
    } catch (err) {
      alert('Erreur upload photo. Réessaie !');
    }
    setUploadingPhoto(false);
  };

  const partagerLien = async () => {
    if (!profil) return;
    const lienParrainage = `https://wooki.app/invite/${encodeURIComponent(profil.prenom)}`;
    const text = `Salut ! Je t'invite sur Wooki 🌿\n\nSorties en plein air entre familles. Gratuit !\n\n👉 ${lienParrainage}`;
    try {
      if (navigator.share) await navigator.share({ title: 'Rejoins Wooki !', text, url: lienParrainage });
      else { await navigator.clipboard.writeText(text); alert('Lien copié !'); }
    } catch (e) { try { await navigator.clipboard.writeText(text); alert('Copié !'); } catch (e2) {} }
  };

  if (!profil) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAFAF7' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🌿</div>
        <div style={{ fontFamily: 'Fredoka One, cursive', color: '#3D9A6A' }}>Chargement...</div>
      </div>
    </div>
  );

  const streak = calculerStreak(participations);
  const activitesFutures = activitesCrees.filter(a => new Date(a.date) >= new Date());
  const activitesPassees = activitesCrees.filter(a => new Date(a.date) < new Date());
  const notifNonLues = notifications.filter(n => !n.lu).length;
  const nbFilleuls = profil.nb_filleuls || 0;

  return (
    <>
      <style>{`
        @keyframes badgePop{0%{transform:scale(0);opacity:0}70%{transform:scale(1.2)}100%{transform:scale(1);opacity:1}}
        @keyframes streakPop{0%{transform:scale(0.5) rotate(-10deg);opacity:0}70%{transform:scale(1.15) rotate(3deg)}100%{transform:scale(1) rotate(0);opacity:1}}
      `}</style>

      {/* MODAL DÉCONNEXION */}
      {showLogoutModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ background: 'white', borderRadius: 24, padding: '28px 24px', width: '100%', maxWidth: 340, textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>👋</div>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.2rem', color: '#1C2B1E', marginBottom: 8 }}>Tu veux te déconnecter ?</div>
            <div style={{ fontSize: '0.85rem', color: '#8A9E8D', marginBottom: 24 }}>Tu pourras te reconnecter avec ton numéro.</div>
            <button onClick={() => { logout(); window.location.href = '/'; }} style={{ width: '100%', padding: '14px', background: '#FF7043', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer', marginBottom: 10 }}>
              Oui, me déconnecter
            </button>
            <button onClick={() => setShowLogoutModal(false)} style={{ width: '100%', padding: '13px', background: 'transparent', color: '#8A9E8D', border: '2px solid #E8EFE9', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}>
              Annuler
            </button>
          </div>
        </div>
      )}

      <div style={{ minHeight: '100vh', background: '#FAFAF7', paddingBottom: 100 }}>

        {/* HERO */}
        <div style={{ background: 'linear-gradient(160deg, #1C2B1E, #2D7A52)', padding: '48px 24px 80px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, #52C289, transparent)', opacity: 0.2, top: -60, right: -40 }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28, position: 'relative', zIndex: 10 }}>
            <div onClick={() => navigate('/feed')} style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.3rem', cursor: 'pointer' }}>
              <span style={{ color: '#52C289' }}>Woo</span><span style={{ color: '#FF7043' }}>ki</span>
            </div>
            <button onClick={() => setShowLogoutModal(true)} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 100, padding: '8px 18px', color: 'white', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
              Déconnexion
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 20, position: 'relative', zIndex: 10 }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div onClick={() => fileRef.current?.click()} style={{ width: 88, height: 88, borderRadius: '50%', overflow: 'hidden', border: '3px solid rgba(255,255,255,0.3)', cursor: 'pointer', background: '#EDF7F1', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                {uploadingPhoto ? (
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}>
                    <div style={{ color: 'white', fontSize: '0.7rem', fontWeight: 700 }}>...</div>
                  </div>
                ) : profil.avatar_url ? (
                  <img
                    src={profil.avatar_url}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <span style={{ fontSize: '2.5rem' }}>👤</span>
                )}
              </div>
              <div onClick={() => fileRef.current?.click()} style={{ position: 'absolute', bottom: 0, right: 0, width: 28, height: 28, borderRadius: '50%', background: '#FF7043', border: '2px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.75rem' }}>📷</div>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhoto} />
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.6rem', color: 'white', marginBottom: 4 }}>{profil.prenom}</div>
              <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.65)', marginBottom: 10 }}>
                {enfants.length > 0 ? enfants.map(e => `${e.prenom} (${e.age} ans)`).join(' · ') : 'Aucun enfant ajouté'}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { val: activitesCrees.length, label: 'créées', color: '#52C289' },
                  { val: activitesRejointes.length, label: 'rejointes', color: '#FF7043' },
                  { val: badges.length, label: 'badges', color: '#FFB300' },
                ].map((s, i) => (
                  <div key={i} style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 10, padding: '5px 12px', textAlign: 'center' }}>
                    <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem', color: s.color }}>{s.val}</div>
                    <div style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.6)', fontWeight: 700 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* STREAK */}
        {streak > 0 && (
          <div style={{ margin: '-28px 20px 12px', background: 'linear-gradient(135deg, #FF7043, #FF5722)', borderRadius: 24, padding: '16px 20px', boxShadow: '0 8px 24px rgba(255,112,67,0.4)', position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ fontSize: '2.8rem', animation: 'streakPop 0.5s ease forwards' }}>🔥</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.3rem', color: 'white' }}>{streak} mois de suite !</div>
              <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.85)' }}>
                {streak === 1 ? 'Continue ce mois-ci !' : `Tu participes depuis ${streak} mois ! Bravo 🌿`}
              </div>
            </div>
          </div>
        )}

        {/* BADGES */}
        {badges.length > 0 && (
          <div style={{ margin: streak > 0 ? '0 20px 12px' : '-28px 20px 12px', background: 'white', borderRadius: 24, padding: '16px', boxShadow: '0 4px 24px rgba(28,43,30,0.1)', position: 'relative', zIndex: 1 }}>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.95rem', color: '#1C2B1E', marginBottom: 12 }}>🏅 Mes badges</div>
            <div style={{ display: 'flex', gap: 10, overflowX: 'auto', scrollbarWidth: 'none' }}>
              {badges.map((b, i) => (
                <div key={i} style={{ flexShrink: 0, textAlign: 'center', animation: `badgePop 0.4s ease ${i * 0.1}s both` }}>
                  <div style={{ width: 52, height: 52, borderRadius: 16, background: '#EDF7F1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem', marginBottom: 4 }}>{b.emoji}</div>
                  <div style={{ fontSize: '0.62rem', color: '#4A6355', fontWeight: 700, maxWidth: 60, lineHeight: 1.3 }}>{b.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PARRAINAGE */}
        <div style={{ margin: '0 20px 12px', background: 'linear-gradient(135deg, #EDF7F1, #D4EEE0)', borderRadius: 24, padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ fontSize: '1.8rem' }}>🌟</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem', color: '#1C2B1E' }}>Programme Ambassadeur</div>
              <div style={{ fontSize: '0.75rem', color: '#4A6355' }}>Invite des amis et débloque des badges !</div>
            </div>
          </div>
          <div style={{ background: 'white', borderRadius: 16, padding: '12px 14px', marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1C2B1E' }}>
                {nbFilleuls === 0 ? 'Invite ton premier ami !' : `${nbFilleuls} ami${nbFilleuls > 1 ? 's' : ''} invité${nbFilleuls > 1 ? 's' : ''} !`}
              </div>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.9rem', color: '#3D9A6A' }}>{nbFilleuls}/3</div>
            </div>
            <div style={{ height: 8, background: '#E8EFE9', borderRadius: 100, overflow: 'hidden' }}>
              <div style={{ height: '100%', background: 'linear-gradient(90deg, #3D9A6A, #52C289)', borderRadius: 100, width: `${Math.min(nbFilleuls / 3 * 100, 100)}%`, transition: 'width 0.5s ease' }} />
            </div>
            {nbFilleuls >= 3 ? (
              <div style={{ fontSize: '0.72rem', color: '#3D9A6A', marginTop: 6, fontWeight: 700 }}>🌟 Badge Ambassadeur débloqué !</div>
            ) : (
              <div style={{ fontSize: '0.72rem', color: '#8A9E8D', marginTop: 6 }}>Encore {3 - nbFilleuls} invitation{3 - nbFilleuls > 1 ? 's' : ''} pour le badge 🌟</div>
            )}
          </div>
          <button onClick={partagerLien} style={{ width: '100%', padding: '13px', background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer', boxShadow: '0 4px 16px rgba(61,154,106,0.3)' }}>
            👋 Inviter un ami sur Wooki
          </button>
        </div>

        {/* LIEN PROFIL PUBLIC */}
        <div style={{ margin: '0 20px 12px', background: '#EDF7F1', borderRadius: 20, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: '1.2rem' }}>🔗</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1C2B1E' }}>Ton profil public</div>
            <div style={{ fontSize: '0.72rem', color: '#3D9A6A' }}>wooki.app/famille/{profil.prenom}</div>
          </div>
          <button onClick={async () => {
            const url = `https://wooki.app/famille/${profil.prenom}`;
            try {
              if (navigator.share) await navigator.share({ title: 'Mon profil Wooki', url });
              else { await navigator.clipboard.writeText(url); alert('Lien copié !'); }
            } catch (e) {}
          }} style={{ background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, padding: '7px 14px', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
            Partager
          </button>
        </div>

        {/* ONGLETS */}
        <div style={{ margin: '0 20px 16px', background: 'white', borderRadius: 24, overflow: 'hidden', boxShadow: '0 4px 24px rgba(28,43,30,0.08)' }}>
          <div style={{ display: 'flex', borderBottom: '1px solid #F3F4F2' }}>
            {[
              { key: 'activites', label: '🌿 Mes sorties', count: activitesCrees.length },
              { key: 'rejointes', label: '✅ Rejointes', count: activitesRejointes.length },
              { key: 'notifs', label: '🔔 Notifs', count: notifNonLues },
            ].map(o => (
              <button key={o.key} onClick={() => setOnglet(o.key)} style={{
                flex: 1, padding: '14px 8px', border: 'none', cursor: 'pointer', background: 'none',
                fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.75rem',
                color: onglet === o.key ? '#3D9A6A' : '#8A9E8D',
                borderBottom: onglet === o.key ? '2.5px solid #3D9A6A' : '2.5px solid transparent',
              }}>
                {o.label}
                {o.count > 0 && <span style={{ marginLeft: 4, background: onglet === o.key ? '#3D9A6A' : '#F3F4F2', color: onglet === o.key ? 'white' : '#8A9E8D', borderRadius: 100, padding: '1px 6px', fontSize: '0.65rem', fontWeight: 900 }}>{o.count}</span>}
              </button>
            ))}
          </div>

          <div style={{ padding: '16px' }}>
            {onglet === 'activites' && (
              <div>
                {activitesFutures.length > 0 && <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#8A9E8D', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>À venir</div>}
                {activitesFutures.map(a => (
                  <div key={a.id} onClick={() => navigate(`/detail/${a.id}`)} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F3F4F2', cursor: 'pointer' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 14, background: '#EDF7F1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', flexShrink: 0 }}>{a.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.9rem', color: '#1C2B1E' }}>{a.titre}</div>
                      <div style={{ fontSize: '0.72rem', color: '#8A9E8D' }}>{new Date(a.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })} · {a.heure?.slice(0,5)}</div>
                    </div>
                    <div style={{ color: '#3D9A6A', fontSize: '0.8rem' }}>→</div>
                  </div>
                ))}
                {activitesPassees.length > 0 && (
                  <>
                    <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#8A9E8D', textTransform: 'uppercase', letterSpacing: 1, marginTop: 16, marginBottom: 10 }}>Passées</div>
                    {activitesPassees.map(a => (
                      <div key={a.id} onClick={() => navigate(`/detail/${a.id}`)} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F3F4F2', cursor: 'pointer', opacity: 0.6 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 14, background: '#F3F4F2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', flexShrink: 0 }}>{a.emoji}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.9rem', color: '#1C2B1E' }}>{a.titre}</div>
                          <div style={{ fontSize: '0.72rem', color: '#8A9E8D' }}>{new Date(a.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</div>
                        </div>
                        <div style={{ color: '#8A9E8D', fontSize: '0.8rem' }}>→</div>
                      </div>
                    ))}
                  </>
                )}
                {activitesCrees.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '32px 0' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>🌱</div>
                    <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem', color: '#1C2B1E', marginBottom: 6 }}>Pas encore d'activités créées</div>
                    <button onClick={() => navigate('/creer')} style={{ padding: '12px 24px', background: '#FF7043', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.88rem', cursor: 'pointer' }}>
                      ＋ Créer une activité
                    </button>
                  </div>
                )}
              </div>
            )}

            {onglet === 'rejointes' && (
              <div>
                {activitesRejointes.map((a, i) => (
                  <div key={a.id} onClick={() => navigate(`/detail/${a.id}`)} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: i < activitesRejointes.length - 1 ? '1px solid #F3F4F2' : 'none', cursor: 'pointer' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 14, background: '#EDF7F1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', flexShrink: 0 }}>{a.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.9rem', color: '#1C2B1E' }}>{a.titre}</div>
                      <div style={{ fontSize: '0.72rem', color: '#8A9E8D' }}>{new Date(a.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })} · {a.lieu?.split(',')[0]}</div>
                    </div>
                    <div style={{ color: '#3D9A6A', fontSize: '0.8rem' }}>→</div>
                  </div>
                ))}
                {activitesRejointes.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '32px 0' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>🎯</div>
                    <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem', color: '#1C2B1E', marginBottom: 6 }}>Pas encore de sorties rejointes</div>
                    <button onClick={() => navigate('/feed')} style={{ padding: '12px 24px', background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.88rem', cursor: 'pointer' }}>
                      Voir les activités
                    </button>
                  </div>
                )}
              </div>
            )}

            {onglet === 'notifs' && (
              <div>
                {notifications.map((n, i) => (
                  <div key={i} onClick={() => n.activite_id && navigate(`/detail/${n.activite_id}`)}
                    style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 0', borderBottom: '1px solid #F3F4F2', cursor: n.activite_id ? 'pointer' : 'default', opacity: n.lu ? 0.6 : 1 }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: n.type === 'participation' ? '#EDF7F1' : n.type === 'parrainage' ? '#FFFBE6' : '#FFF3EE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0 }}>
                      {n.type === 'participation' ? '🎉' : n.type === 'desinscription' ? '😔' : n.type === 'parrainage' ? '🌟' : n.type === 'badge' ? '🏅' : '🔔'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.85rem', color: '#1C2B1E', lineHeight: 1.5, fontWeight: n.lu ? 400 : 700 }}>{n.message}</div>
                      <div style={{ fontSize: '0.68rem', color: '#8A9E8D', marginTop: 4 }}>
                        {new Date(n.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    {!n.lu && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#FF7043', flexShrink: 0, marginTop: 6 }} />}
                  </div>
                ))}
                {notifications.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '32px 0' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>🔔</div>
                    <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem', color: '#1C2B1E', marginBottom: 6 }}>Pas encore de notifications</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ENFANTS */}
        <div style={{ margin: '0 20px 12px', background: 'white', borderRadius: 24, padding: '16px', boxShadow: '0 4px 24px rgba(28,43,30,0.06)' }}>
          <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.95rem', color: '#1C2B1E', marginBottom: 12 }}>🧒 Mes enfants</div>
          {enfants.map((e, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: i < enfants.length - 1 ? '1px solid #F3F4F2' : 'none' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#EDF7F1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>🧒</div>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.9rem', color: '#1C2B1E' }}>{e.prenom}</div>
              <div style={{ fontSize: '0.78rem', color: '#8A9E8D', marginLeft: 'auto' }}>{e.age} ans</div>
            </div>
          ))}
          {enfants.length === 0 && <div style={{ fontSize: '0.85rem', color: '#8A9E8D' }}>Aucun enfant ajouté.</div>}
        </div>

      </div>
    </>
  );
}